import { BusinessObjectPart, BusinessObjectPartContext, PropertyChangedJoinPoint, Weaver } from '@skylab-shared/core';
import { AddressModel, DocumentPaymentListItemModel, ExchangeRateModel, KsefDocumentStatus, KsefSendSubmissionMode, SaleAdvanceAdjustmentDocumentModel, SaleAdvanceDocumentItemModel, SaleAdvanceDocumentModel, SaleDocumentAdjustmentModel, SaleDocumentItemModel, SaleDocumentModel } from '@skylab-shared/dataModel';
import { dateDiffInDays } from '@skylab-shared/utils';
import { propertyChangedJoinPointFilter } from '../../common/property-changed/property-changed-filter';
import { CommunityTransactionForSaleQuery } from '../../community-transaction/queries/community-transaction-for-sale.query-base';
import { isAdvanceModel, isDocumentToKsef, isDocumentToKsefOnlineMode, isKsefDocumentSendPending, isKsefDocumentSendWithSuccess, isKsefDocumentType } from '../utils/sale-utils';
import { DocumentShouldBeToKsefWarning } from '../violations/document-should-be-to-ksef-warning';
import { KsefDocumentCannotBeWithWstoOssTransaction } from '../violations/ksef-document-cannot-be-with-wsto-oss-transaction-error';
import { KsefDocumentIssueDateShouldBeNotFromFutureError } from '../violations/ksef-document-issue-date-should-be-not-from-future-error';
import { KsefDocumentWithPastIssueDateWillBeOfflineWarning } from '../violations/ksef-document-with-past-issue-date-will-be-offline-warning';


type SaleDocumentKsefModelPart = SaleDocumentModel & SaleAdvanceDocumentModel & SaleDocumentAdjustmentModel & SaleAdvanceAdjustmentDocumentModel;

export class SaleDocumentKsefBoPart extends BusinessObjectPart<SaleDocumentKsefModelPart> {
    public constructor(
        weaver: Weaver,
        private getPreviousDocument?: () => { ksefSubmissionMode: KsefSendSubmissionMode }
    ) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<SaleDocumentKsefModelPart>): Promise<void> {
        await super.initializeAsync(context);

        this.registerReadonlyProperties();
        this.registerChangingProperties();
        this.registerValidationRules();
    }

    private registerValidationRules(): void {
        const transactions = this.bo.localContext.getPreloaded(CommunityTransactionForSaleQuery);
        const shouldNotViolateDate = () => !isDocumentToKsef(this.bo.model) || !isKsefDocumentType(this.bo.model.kind) || isKsefDocumentSendPending(this.bo.model) || isKsefDocumentSendWithSuccess(this.bo.model)

        this.engine.validation.addRule(
            [[this.modelType, 'ksefSubmissionMode'], [this.modelType, 'communityTransactionId']],
            [[this.modelType, 'ksefSubmissionMode']],
            () => {
                if (!isDocumentToKsef(this.bo.model)) return true;

                return !(transactions?.find(elem => elem.symbol === 'WSTO-OSS')?.id === this.bo.model.communityTransactionId);
            },
            KsefDocumentCannotBeWithWstoOssTransaction
        );

        this.engine.validation.addRule(
            [[this.modelType, 'issueDate'], [this.modelType, 'ksefSubmissionMode']],
            [[this.modelType, 'issueDate']],
            () => {
                if(shouldNotViolateDate()) return true;

                return dateDiffInDays(this.bo.model.issueDate, new Date()) <= 0;
            },
            KsefDocumentIssueDateShouldBeNotFromFutureError
        );

        this.engine.validation.addRule(
            [[this.modelType, 'issueDate'], [this.modelType, 'ksefSubmissionMode']],
            [[this.modelType, 'issueDate']],
            () => {
                if(shouldNotViolateDate()) return true;
                if(!isDocumentToKsefOnlineMode(this.bo.model)) return true;

                return dateDiffInDays(this.bo.model.issueDate, new Date()) >= 0;
            },
            KsefDocumentWithPastIssueDateWillBeOfflineWarning
        );

        this.engine.validation.addRule(
            [[this.modelType, 'ksefSubmissionMode']],
            () => {
                if (!this.getPreviousDocument?.()) return true;
                if (isDocumentToKsef(this.bo.model) || isKsefDocumentSendWithSuccess(this.bo.model) || isKsefDocumentSendPending(this.bo.model)) return true;
                return !isDocumentToKsef(this.getPreviousDocument());
            },
            DocumentShouldBeToKsefWarning
        );
    }

    private registerChangingProperties(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['ksefSubmissionMode'])(
            () => {
                if (isDocumentToKsef(this.bo.model) && !this.bo.model.ksefDocumentStatus) this.bo.model.ksefDocumentStatus = KsefDocumentStatus.ToSend;
            }
        )));
    }

    private registerReadonlyProperties(): void {
        this.engine.readonly.setAsReadonly([this.modelType, 'ksefDocumentId']);
        this.engine.readonly.setAsReadonly([this.modelType, 'ksefCollectiveIdentifier']);

        if (!(this.bo.model.isPersistent && isKsefDocumentType(this.bo.model.kind))) return;

        this.engine.readonly.addRule([
            [this.modelType, 'inventoryEffect']
        ], () => isKsefDocumentSendPending(this.bo.model));

        this.engine.readonly.addRule([
            [this.modelType, 'numberingId'],
            [this.modelType, 'documentNumber'],
            [this.modelType, 'issueDate'],
        ], () => this.bo.model.isPersistent && !!this.bo.model.ksefXmlImported);

        this.engine.readonly.addRule([
            [this.modelType, 'communityTransactionId'],
            [this.modelType, 'transactionLocation'],
            [this.modelType, 'issueDate'],
            [this.modelType, 'saleDate'],
            [this.modelType, 'goodsIssueDate'],
            [this.modelType, 'dueDate'],
            [this.modelType, 'paymentMethodId'],
            [this.modelType, 'bankAccountNumberId'],
            [this.modelType, 'baseCurrencyBankAccountNumberId'],
            [this.modelType, 'issuer'],
            [this.modelType, 'recipient'],
            [this.modelType, 'client'],
            [this.modelType, 'buyer'],
            [this.modelType, 'marginSchemeId'],
            [this.modelType, 'vatReleaseReasonId'],
            [this.modelType, 'vatReleaseReasonCustomName'],
            [this.modelType, 'vatReleaseReasonSource'],
            [this.modelType, 'calculationMethod'],
            [this.modelType, 'currencyId'],
            [this.modelType, 'jpkVatGroups'],
            [this.modelType, 'dueDateInDays'],
            [this.modelType, 'numberingId'],
            [this.modelType, 'issuedByVatPayer'],
            [this.modelType, 'documentNumber'],
            [this.modelType, 'customDueDateDescription'],
            [this.modelType, 'note'],
            [this.modelType, 'splitPayment'],
            [this.modelType, 'advanceValue'],
            [this.modelType, 'advanceReturnValue'],
            [this.modelType, 'adjustmentReasonId'],
            [this.modelType, 'wstoAdditionalInformation'],
            [this.modelType, 'wstoConsumerCountryId'],
            [this.modelType, 'wstoShipmentCountryId'],
            [this.modelType, 'ksefSubmissionMode'],
            [this.modelType, 'orderNumber'],
            [this.modelType, 'orderDate'],
            [this.modelType, 'agreementNumber'],
            [this.modelType, 'agreementDate'],
            [this.modelType, 'reverseCharge'],
            [this.modelType, 'adjustPaymentDate'],
            [this.modelType, 'rateNP']
        ], () => isKsefDocumentSendWithSuccess(this.bo.model) || isKsefDocumentSendPending(this.bo.model));

        this.engine.readonly.addRule([[DocumentPaymentListItemModel, 'onDocument']], () => isKsefDocumentSendWithSuccess(this.bo.model));
        this.engine.readonly.addRule([[DocumentPaymentListItemModel, 'date']], (ctx) => isKsefDocumentSendWithSuccess(this.bo.model) && ctx.source.onDocument);
        this.engine.readonly.setReadonlyOnSubtree(DocumentPaymentListItemModel, () => isKsefDocumentSendPending(this.bo.model));

        const itemModel = isAdvanceModel(this.bo.model) ? SaleAdvanceDocumentItemModel : SaleDocumentItemModel;

        this.engine.readonly.setReadonlyOnSubtree(itemModel, () => isKsefDocumentSendWithSuccess(this.bo.model) || isKsefDocumentSendPending(this.bo.model));
        this.engine.readonly.setReadonlyOnSubtree(AddressModel, () => isKsefDocumentSendWithSuccess(this.bo.model) || isKsefDocumentSendPending(this.bo.model));
        this.engine.readonly.setReadonlyOnSubtree(ExchangeRateModel, () => isKsefDocumentSendWithSuccess(this.bo.model) || isKsefDocumentSendPending(this.bo.model));
    }
}
